import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'apollo-react/components/Box';
import Typography from 'apollo-react/components/Typography';
import CircularProgress from 'apollo-react/components/CircularProgress';
import Divider from 'apollo-react/components/Divider';
import CreditCardBack from 'apollo-react-icons/CreditCardBack';
import axiosInstance from '../../axiosInstance';
import CommonModal from '../common/CommonModal/CommonModal';
import styles from './PaymentConfirmationModal.module.css';

/**
 * Payment Confirmation Modal
 * Flow: Validate → Save Draft → Initiate Payment → Redirect to Gateway
 * The actual request submission happens AFTER successful payment (via webhook)
 */
const PaymentConfirmationModal = ({
  open,
  onClose,
  onValidate,
  onSaveDraft,
  amount,
  currency,
  draftId,
  stageId,
  requestId,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [error, setError] = useState(null);

  // Calculate price breakdown
  const calculatePrice = useCallback(() => {
    if (!open) return;

    setIsCalculating(true);
    setError(null);

    try {
      const basePrice = Number(amount) || 0;
      const taxPercent = 0; // It's 0% for now until we have a tax rate
      const tax = Math.round((basePrice * taxPercent * 100) / 100) / 100;
      const total = basePrice + tax;

      setPriceBreakdown({
        currency: currency || 'USD',
        basePrice,
        taxPercent,
        tax,
        total,
        subtotal: basePrice,
      });
    } catch (err) {
      console.error('Error calculating price:', err);
      setPriceBreakdown(null);
    } finally {
      setIsCalculating(false);
    }
  }, [open, amount, currency]);

  useEffect(() => {
    calculatePrice();
  }, [calculatePrice]);

  const handleConfirmPayment = useCallback(async () => {
    setError(null);
    setIsValidating(true);

    try {
      // Step 1: Validate form data (don't submit yet)
      const validationResult = await onValidate();

      if (!validationResult || validationResult.success === false) {
        setIsValidating(false);
        return;
      }

      setIsValidating(false);
      setIsProcessing(true);

      // Step 2: Save/update draft (this is the backup before payment)
      setProcessingMessage(t('payment.savingDraft'));
      const draftResult = await onSaveDraft();

      if (!draftResult || !draftResult.success) {
        setError(draftResult?.error || t('payment.draftSaveFailed'));
        setIsProcessing(false);
        return;
      }

      const savedDraftId = draftResult.draftId || draftId;

      // Step 3: Initiate payment session with draft ID
      setProcessingMessage(t('payment.initiatingPayment'));
      const paymentResponse = await axiosInstance.post(
        '/api/payments/sessions',
        {
          draft_id: savedDraftId,
          stage_id: stageId,
          ...(requestId ? { request_id: requestId } : {}),
        }
      );

      const { iframe_url, transaction_id, request_id } =
        paymentResponse.data.data || {};

      // Store payment info for processing page (sessionStorage for security)
      sessionStorage.setItem('payment_transaction_id', transaction_id);
      sessionStorage.setItem(
        'payment_request_id',
        request_id || requestId || ''
      );
      sessionStorage.setItem('payment_draft_id', savedDraftId);

      // Step 4: Redirect to payment gateway
      setProcessingMessage(t('payment.redirecting'));
      if (iframe_url) {
        window.location.href = iframe_url;
      }
    } catch (err) {
      console.error('Payment confirmation error:', err);
      setError(
        err.response?.data?.message || err.message || t('payment.errorOccurred')
      );
      setIsValidating(false);
      setIsProcessing(false);
      setProcessingMessage('');
    }
  }, [onValidate, onSaveDraft, draftId, stageId, requestId, t]);

  const formatCurrencyValue = (value) => {
    return `${Number(value).toFixed(2)} ${priceBreakdown?.currency || currency || 'USD'}`;
  };

  if (isProcessing) {
    return (
      <CommonModal
        open={open}
        onClose={() => {}}
        title={t('payment.processing')}
        className={styles.modal}
        hideButtons
        hideHeader
      >
        <Box className={styles.processingOverlay}>
          <CircularProgress variant="indeterminate" size={60} />
          <Typography variant="body1" className={styles.processingMessage}>
            {processingMessage}
          </Typography>
          <Typography variant="caption" className={styles.processingWarning}>
            {t('payment.doNotClose')}
          </Typography>
        </Box>
      </CommonModal>
    );
  }

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={t('payment.confirmTitle')}
      className={styles.modal}
      buttonProps={[
        {
          label: t('Cancel'),
          onClick: onClose,
          disabled: isSubmitting || isProcessing || isValidating,
        },
        {
          label: isValidating
            ? t('payment.validating')
            : isSubmitting
              ? t('payment.processing')
              : t('payment.proceedToPayment'),
          onClick: handleConfirmPayment,
          disabled:
            isSubmitting ||
            isProcessing ||
            isValidating ||
            isCalculating ||
            !priceBreakdown,
          variant: 'contained',
          color: 'primary',
        },
      ]}
    >
      <Box className={styles.modalContent}>
        {/* Header */}
        <Box className={styles.modalHeader}>
          <CreditCardBack className={styles.paymentIcon} />
          <Typography variant="body1">{t('payment.confirmMessage')}</Typography>
        </Box>

        {error && (
          <Box className={styles.errorBanner}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {/* Price Breakdown */}
        <Box className={styles.priceBreakdownSection}>
          <Typography variant="h6" className={styles.sectionTitle}>
            {t('payment.paymentSummary')}
          </Typography>

          {isCalculating ? (
            <Box className={styles.priceLoading}>
              <CircularProgress variant="indeterminate" size={24} />
              <Typography variant="body2">
                {t('payment.calculating')}
              </Typography>
            </Box>
          ) : priceBreakdown ? (
            <>
              <Box className={styles.priceRow}>
                <Typography variant="body2">
                  {t('payment.basePrice')}
                </Typography>
                <Typography variant="body2" className={styles.priceValue}>
                  {formatCurrencyValue(priceBreakdown.basePrice)}
                </Typography>
              </Box>

              <Divider className={styles.priceDivider} />

              <Box className={styles.priceRow}>
                <Typography variant="body2">{t('payment.subtotal')}</Typography>
                <Typography variant="body2" className={styles.priceValue}>
                  {formatCurrencyValue(priceBreakdown.subtotal)}
                </Typography>
              </Box>

              <Box className={styles.priceRow}>
                <Typography variant="body2">
                  {t('payment.tax')} ({priceBreakdown.taxPercent}%)
                </Typography>
                <Typography variant="body2" className={styles.priceValue}>
                  {formatCurrencyValue(priceBreakdown.tax)}
                </Typography>
              </Box>

              <Divider className={styles.priceDivider} />

              <Box className={`${styles.priceRow} ${styles.totalRow}`}>
                <Typography variant="body1" className={styles.totalLabel}>
                  {t('payment.total')}
                </Typography>
                <Typography variant="h5" className={styles.totalValue}>
                  {formatCurrencyValue(priceBreakdown.total)}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="error">
              {t('payment.unableToCalculatePrice')}
            </Typography>
          )}
        </Box>

        {/* Info Notice */}
        <Box className={styles.paymentInfoNotice}>
          <Typography variant="caption">{t('payment.infoNotice')}</Typography>
        </Box>
      </Box>
    </CommonModal>
  );
};

export default PaymentConfirmationModal;
